import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type {
  IProcessesListQuery,
  IProcessesPage,
  ProcessesService,
} from "@/services/projects-v3.service";
import type { TProcess } from "@/types/project";
import { getQueryClient } from "./client";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, scopeIfReady, type QueryScope } from "./scope";

/**
 * The workspace's processes as two shared reference queries (NG-2799):
 *
 * - **bank**, `GET {ws}/processes/`, `[slug, uid, 'processes', 'bank']`: every
 *   process with its stages. What a reader shows for a process or stage a
 *   project already holds (the projects list, the template form), so it is
 *   deliberately not narrowed by `can_use` (NG-679). Gated on `process.read`,
 *   which a Standard member does not hold by default (NG-1950).
 * - **project stages**, `GET {ws}/projects/{pid}/stage-options/`,
 *   `[slug, uid, 'processes', 'project-stages', pid, processId]`: one project's
 *   process stages, gated on `project.read`. Every work-item view reads its
 *   project's stages here, never from the bank, so a member without
 *   `process.read` sees them too (NG-2893).
 * - **pickable**, `GET {ws}/processes/pickable/`,
 *   `[slug, uid, 'processes', 'pickable']`: the `{id, name}` rows this caller
 *   may pick for a new or edited project. The create-project dialog, the
 *   request convert modal and the project properties panel offer these.
 *
 * - **usage**, `GET {ws}/processes/{id}/usage/`,
 *   `[slug, uid, 'processes', 'usage', id]`: what still points at one process
 *   (projects, templates, work items per stage). The editor and the delete
 *   dialog judge which removals to offer on it, so it is never reused stale:
 *   re-read on every mount and every enable, never on a timer.
 *
 * - **table pages**, `GET {ws}/processes/?q=…&cursor=…`,
 *   `[slug, uid, 'processes', 'page', {q, type, stages, status, order_by}]`:
 *   Admin Settings → Processes, walked by cursor (NG-2805). **Mutable**: reused
 *   for 30 s and re-read on screen.
 *
 * All sit under `'processes'`, so one prefix invalidation reaches every one.
 * The bank and pickable list are **reference** (contract §2): reused for 5 min,
 * refetched on focus and reconnect. Each is a plain idempotent GET, so it
 * declares `"safe-read"` and `runRead` owns its retries.
 *
 * @writers `ProcessesStore`'s process and stage writers mark them all stale
 * after a confirmed write (`invalidateCachedProcesses`) and resolve once the
 * ones on screen have re-read, so the settings table shows the server's row,
 * its place in the sort and whether the filters still admit it (a retired
 * process leaves an Active filter). The stages editor also marks usage stale
 * after a write the server refused (`useProcessUsage`).
 */

/** The prefix both process lists sit under. */
export function processesKey(scope: QueryScope) {
  return scopedKey(scope, "processes");
}

/** The bank key. */
function processBankKey(scope: QueryScope) {
  return scopedKey(scope, "processes", "bank");
}

/** The bank: every process in `scope`'s workspace, with its stages. */
export function processBankQuery(service: Pick<ProcessesService, "list">, scope: QueryScope) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: processBankKey(scope),
    queryFn: scopedRead((request) => service.list(scope.tenant, request), {
      retry: "safe-read",
    }),
  });
}

/** The pickable key. */
function pickableProcessesKey(scope: QueryScope) {
  return scopedKey(scope, "processes", "pickable");
}

/** The processes this caller may pick, as `{id, name}` rows. */
export function pickableProcessesQuery(
  service: Pick<ProcessesService, "listPickable">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: pickableProcessesKey(scope),
    queryFn: scopedRead((request) => service.listPickable(scope.tenant, request), {
      retry: "safe-read",
    }),
  });
}

/** One project's stages. Under `'processes'`, so a process or stage write and
 *  the processes hint mark it with the bank; keyed by the process too, so
 *  switching the project's process reads afresh. */
function projectStagesKey(scope: QueryScope, projectId: string, processId: string) {
  return scopedKey(scope, "processes", "project-stages", projectId, processId);
}

/** The stages of `processId`, the process `projectId` runs (see above). */
export function projectStagesQuery(
  service: Pick<ProcessesService, "listProjectStages">,
  scope: QueryScope,
  projectId: string,
  processId: string,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: projectStagesKey(scope, projectId, processId),
    queryFn: scopedRead((request) => service.listProjectStages(scope.tenant, projectId, request), {
      retry: "safe-read",
    }),
  });
}

/** The usage key for one process. */
function processUsageKey(scope: QueryScope, processId: string) {
  return scopedKey(scope, "processes", "usage", processId);
}

/** What still points at `processId`. Never served stale (see above). */
export function processUsageQuery(
  service: Pick<ProcessesService, "usage">,
  scope: QueryScope,
  processId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    staleTime: 0,
    refetchInterval: false,
    refetchOnMount: "always",
    queryKey: processUsageKey(scope, processId),
    queryFn: scopedRead((request) => service.usage(scope.tenant, processId, request), {
      retry: "safe-read",
    }),
  });
}

/** The settings table's server filters: search, type, stage names, status and sort. */
export type ProcessesPageQuery = Omit<IProcessesListQuery, "cursor" | "per_page">;

/**
 * The table key. Absent and empty parameters send the same request, so they
 * share an entry; the filters are sets, so their order does not split one.
 */
function processesPageKey(scope: QueryScope, query: ProcessesPageQuery = {}) {
  return scopedKey(scope, "processes", "page", {
    q: query.q ?? "",
    type: [...(query.type ?? [])].sort(),
    stages: [...(query.stages ?? [])].sort(),
    status: [...(query.status ?? [])].sort(),
    order_by: query.order_by ?? "",
  });
}

/**
 * The settings table's pages. The first page asks with no cursor; each later
 * page asks with the cursor the previous one returned.
 */
export function processesPageQuery(
  service: Pick<ProcessesService, "listProcessesPage">,
  scope: QueryScope,
  query: ProcessesPageQuery = {},
) {
  return infiniteQueryOptions({
    ...FRESHNESS.mutable,
    queryKey: processesPageKey(scope, query),
    initialPageParam: null as string | null,
    queryFn: scopedRead<IProcessesPage, string | null>(
      (request, { pageParam }) =>
        service.listProcessesPage(scope.tenant, { ...query, cursor: pageParam }, request),
      { retry: "safe-read" },
    ),
    getNextPageParam: (last: IProcessesPage) =>
      last.next_page_results && last.next_cursor ? last.next_cursor : undefined,
  });
}

/**
 * Warm the settings table's first page for `query` from a route loader, so the
 * table answers from the cache on arrival. Rejects when the read fails, so the
 * loader can log it; the table shows its own error from the same entry.
 */
export async function prefetchProcessesPage(
  service: Pick<ProcessesService, "listProcessesPage">,
  slug: string,
  query: ProcessesPageQuery = {},
): Promise<void> {
  const scope = scopeIfReady(slug, "processes page");
  if (!scope) return;
  await getQueryClient().fetchInfiniteQuery(processesPageQuery(service, scope, query));
}

/** Every process in the pages, once each: a row can reappear on the next page as rows are added. */
export function rowsOfProcessesPages(pages: readonly IProcessesPage[]): TProcess[] {
  const seen = new Set<string>();
  const rows: TProcess[] = [];
  for (const page of pages) {
    for (const process of page.results) {
      if (seen.has(process.id)) continue;
      seen.add(process.id);
      rows.push(process);
    }
  }
  return rows;
}
